import React, { useRef, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Html5Qrcode } from 'html5-qrcode'
import { useTheme } from '../contexts/ThemeContext'
import LanguageSwitcher from './LanguageSwitcher'
import useTranslation from '../hooks/useTranslation'

const SCANNER_ELEMENT_ID = 'qr-scanner-display'

/**
 * QR Scanner Display Component
 * Shows a QR scanner interface when URL parameters are missing or invalid
 * Shows camera in three cases:
 * 1. When user enters from BASEURL only (no params)
 * 2. When user enters wrong/invalid link
 * 3. When user enters HOME without data
 */
const QRScannerDisplay: React.FC = () => {
  const { t, currentLanguage: language } = useTranslation()
  const { headerLogo } = useTheme()
  const isRTL = language === 'ar'
  const navigate = useNavigate()
  const scannerRef = useRef<HTMLDivElement>(null)
  const html5QrcodeRef = useRef<Html5Qrcode | null>(null)
  const scannedRef = useRef(false)
  const [error, setError] = useState<string | null>(null)
  const [scanned, setScanned] = useState(false)
  const [scannedText, setScannedText] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    setScanned(false)
    scannedRef.current = false
    setScannedText(null)

    const stopScanner = async () => {
      const instance = html5QrcodeRef.current
      if (!instance) return
      html5QrcodeRef.current = null
      try {
        if (typeof instance.getState === 'function') {
          const state = instance.getState()
          if (state === 2 || state === 3) {
            await instance.stop()
          }
        } else {
          await instance.stop()
        }
      } catch {
        // Ignore errors when stopping
      }
      try {
        instance.clear()
      } catch {
        // Ignore clear errors
      }
    }

    const startScanner = async () => {
      // Wait one frame so the scanner host element is in the DOM
      await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()))
      if (cancelled) return

      const host = document.getElementById(SCANNER_ELEMENT_ID)
      if (!host || !scannerRef.current) return

      await stopScanner()
      if (cancelled) return

      // Re-check after async stop — StrictMode / error UI can remove the host
      if (!document.getElementById(SCANNER_ELEMENT_ID) || !scannerRef.current) return

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      }

      let html5Qrcode: Html5Qrcode
      try {
        html5Qrcode = new Html5Qrcode(SCANNER_ELEMENT_ID)
      } catch (err) {
        if (!cancelled) {
          setError(
            language === 'ar'
              ? 'تعذر تهيئة ماسح رمز QR. حاول إعادة تحميل الصفحة.'
              : 'Unable to initialize QR scanner. Please reload the page.'
          )
        }
        return
      }

      if (cancelled) {
        try {
          html5Qrcode.clear()
        } catch {
          // ignore
        }
        return
      }

      html5QrcodeRef.current = html5Qrcode

      html5Qrcode
        .start(
          { facingMode: 'environment' },
          config,
          (decodedText: string) => {
            if (cancelled || scannedRef.current) return
            scannedRef.current = true
            setScanned(true)

            // Safely stop the scanner without triggering error
            if (typeof html5Qrcode.getState === 'function') {
              const state = html5Qrcode.getState()
              if (state === 2 || state === 3) {
                html5Qrcode.stop().catch(() => {})
              }
            } else {
              html5Qrcode.stop().catch(() => {})
            }

            if (
              decodedText.startsWith('http') &&
              (decodedText.includes('branchId') && decodedText.includes('typeId'))
            ) {
              setScannedText(decodedText)
              try {
                const urlObj = new URL(decodedText)
                let pathname = urlObj.pathname
                const search = urlObj.search

                if (!pathname.includes('/home')) {
                  pathname = pathname.replace(/\/$/, '') + '/home/'
                } else {
                  pathname = pathname.replace(/\/home\/?/, '/home/')
                }

                const newUrl = `${urlObj.protocol}//${urlObj.host}${pathname}${search}`
                const finalUrl = newUrl.replace(/([^:]\/)\/+/g, '$1')
                window.location.href = finalUrl
              } catch {
                let url = decodedText
                url = url.replace(/([^:]\/)\/+/g, '$1')
                if (!url.includes('/home')) {
                  url = url.replace(/(:\/\/[^\/]+)(\/?)(\?|$)/, '$1/home/$3')
                } else {
                  url = url.replace(/\/home(\?|\/|$)/, '/home/$1')
                }
                url = url.replace(/([^:]\/)\/+/g, '$1')
                window.location.href = url
              }
            } else if (decodedText.includes('branchId') && decodedText.includes('typeId')) {
              setScannedText(decodedText)
              const path = '/home/'
              const cleanParams = decodedText.replace(/^\//, '')
              navigate(path + cleanParams, { replace: true })
            } else {
              setError(
                language === 'ar'
                  ? 'الكود غير صحيح أو لا يحتوي على البيانات المطلوبة.'
                  : 'Invalid code or missing required data.'
              )
              setScanned(false)
              scannedRef.current = false
              setTimeout(() => {
                if (!cancelled && scannerRef.current) {
                  startScanner()
                }
              }, 2000)
            }
          },
          () => {
            // Camera scan error, just ignore unless permission
          }
        )
        .catch((err) => {
          if (cancelled) return
          let msg =
            language === 'ar'
              ? 'تعذر فتح الكاميرا أو لم يُسمح بالوصول إليها. الرجاء التحقق من صلاحيات الكاميرا أو تفعيل HTTPS.'
              : 'Unable to open camera or access denied. Please check camera permissions or enable HTTPS.'
          if (typeof err === 'string' && err.includes('Permission')) {
            msg =
              language === 'ar'
                ? 'تم رفض صلاحية الوصول للكاميرا. يجب السماح بذلك لاستخدام الميزة.'
                : 'Camera access denied. You must allow camera access to use this feature.'
          }
          setError(msg)
        })
    }

    startScanner()

    return () => {
      cancelled = true
      void stopScanner()
    }
    // eslint-disable-next-line
  }, [])

  return (
    <div className="w-full flex flex-col min-h-screen bg-white items-center justify-center ">
      {/* Header with Language Switcher and Logo */}
      <div className="w-full p-4 bg-white shadow-sm flex justify-between items-center gap-5">
        <div className="flex justify-start items-center gap-1">
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex justify-end items-center gap-3">
          {headerLogo ? (
            <img
              src={headerLogo}
              alt={t('restaurant.brandName')}
              className="h-8 object-contain"
            />
          ) : (
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg text-gray-800">
                {t('restaurant.brandName')}
              </span>
              <div className="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center">
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content Area - QR Scanner */}
      <div className="w-full flex-1 flex flex-col justify-center items-center gap-10 px-4">
        <div className="w-full flex flex-col justify-center items-end gap-1.5">
          <div className="w-full flex flex-col justify-center items-end gap-1">
            <div className={`w-full text-center text-gray-800 text-2xl font-medium font-['IBM_Plex_Sans_Arabic'] leading-8 ${isRTL ? 'text-center' : 'text-center'}`}>
              {language === 'ar' ? 'امسح رمز الـ QR' : 'Scan QR Code'}
            </div>
            <div className={`w-full text-center text-gray-600 text-base font-normal font-['IBM_Plex_Sans_Arabic'] leading-6 ${isRTL ? 'text-center' : 'text-centerx'}`}>
              {language === 'ar'
                ? 'يرجى توجيه الكاميرا نحو رمز الـ QR'
                : 'Please point the camera towards the QR code'}
            </div>
          </div>
        </div>

        {/* QR Scanner Frame */}
        <div className="w-full flex justify-center items-start gap-2.5">
          <div className="w-72 h-72 relative">
            <div className="w-72 h-72 absolute left-0 top-0  rounded-lg">
              <div className="absolute top-0 left-1  w-12 h-12 border-t-4 border-l-4 border-gray-950 rounded-tl-3xl"></div>
              <div className="absolute top-0 right-1 w-12 h-12 border-t-4 border-r-4 border-gray-950 text-3xl font-bold rounded-tr-3xl"></div>
              <div className="absolute bottom-0 left-1 w-12 h-12 border-b-4 border-l-4 border-gray-950 rounded-bl-3xl"></div>
              <div className="absolute bottom-0 right-1  w-12 h-12 border-b-4 border-r-4 border-gray-950 rounded-br-3xl"></div>
            </div>
            <div className="w-64 h-64 left-[14px] top-[14px] absolute bg-neutral-200 rounded-[25px] flex items-center justify-center overflow-hidden">
              {/* Keep host mounted always — html5-qrcode requires a stable element id */}
              <div
                id={SCANNER_ELEMENT_ID}
                ref={scannerRef}
                className="w-full h-full"
                style={{ minHeight: '256px', minWidth: '256px' }}
              />
              {error && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-neutral-200 rounded-[25px] px-4">
                  <div className="text-red-500 text-sm text-center">
                    {error}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer - Powered by */}
      <div className="w-full py-4 flex flex-col justify-end items-center gap-2.5">
        <div className="h-8 pl-4 pr-3.5 py-2 flex justify-center items-center">
          <div className="text-black flex flex-row items-center text-xs" dir="ltr">
            <span>Powered by:</span>
            <img src='/Logo_power.svg' alt='logo' className='h-17 w-17 pl-3' />
          </div>
        </div>
      </div>
    </div>
  )
}

export default QRScannerDisplay
